/**
 * StudioBilling — channel invoices & PDF export.
 *
 * Owner can generate this month's invoice for any owned channel, view a
 * history, mark invoices paid, and download a branded PDF.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  FileText, Download, Loader2, Plus, Check, AlertTriangle,
  Receipt, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StudioBilling() {
  const [channels, setChannels] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [activeId, setActiveId] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [c, inv] = await Promise.all([
        axios.get(`${API}/api/studio/channels`, { withCredentials: true }),
        axios.get(`${API}/api/studio/billing/invoices`, { withCredentials: true }),
      ]);
      const owned = c.data?.owned || [];
      setChannels(owned);
      if (!activeId && owned[0]) setActiveId(owned[0].channel_id);
      setInvoices(inv.data?.invoices || []);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const onGenerate = async () => {
    if (!activeId) return;
    setGenBusy(true);
    try {
      await axios.post(
        `${API}/api/studio/billing/generate`,
        { channel_id: activeId },
        { withCredentials: true }
      );
      toast.success("Invoice generated");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate invoice");
    } finally {
      setGenBusy(false);
    }
  };

  const onMarkPaid = async (id) => {
    try {
      await axios.post(`${API}/api/studio/billing/mark-paid`, { invoice_id: id }, { withCredentials: true });
      toast.success("Invoice marked paid");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update");
    }
  };

  const onDownload = (inv) => {
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return;
    const today = new Date(inv.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
    const rows = (inv.line_items || []).map(
      (li) => `<tr><td>${li.label}</td><td style="text-align:right;">${li.qty}</td>
        <td style="text-align:right;">₹${li.unit.toLocaleString("en-IN")}</td>
        <td style="text-align:right;">₹${li.total.toLocaleString("en-IN")}</td></tr>`
    ).join("");
    w.document.write(`
      <html><head><title>Invoice ${inv.invoice_no}</title>
      <style>
        body { font-family: -apple-system, system-ui, sans-serif; padding: 32px 40px; color:#1f1235; background:#fff; }
        .brand-bar {
          display:flex; align-items:flex-start; justify-content:space-between;
          padding-bottom: 18px; margin-bottom: 26px;
          border-bottom: 3px solid #7c3aed;
        }
        .logo {
          font-size: 26px; font-weight: 800;
          background: linear-gradient(135deg, #7c3aed, #ec7c5e);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }
        .meta-r { text-align:right; font-size:13px; color:#444; }
        .meta-r strong { display:block; font-size:18px; color:#1f1235; margin-bottom:4px; }
        h2 { font-size:14px; color:#7c3aed; text-transform:uppercase; letter-spacing:0.08em; margin: 26px 0 8px; }
        .bill-to { background:#faf7ff; padding:14px 16px; border-radius:8px; border:1px solid rgba(124,58,237,0.12); }
        table { width:100%; border-collapse: collapse; margin: 10px 0 4px; font-size:13.5px; }
        th { background: linear-gradient(135deg, #7c3aed, #a78bfa); color:#fff; padding: 10px 12px; text-align:left; font-weight:600; text-transform:uppercase; font-size:11px; letter-spacing:0.06em; }
        td { padding: 10px 12px; border-bottom: 1px solid #eee; }
        .totals { display:flex; justify-content:flex-end; margin-top: 14px; }
        .totals table { width: 320px; }
        .totals td { border:none; padding: 5px 0; }
        .totals .grand { border-top: 2px solid #7c3aed; font-size: 17px; font-weight: 700; color:#1f1235; padding-top: 8px; }
        .status-paid { display:inline-block; padding: 4px 12px; background:#dcfce7; color:#15803d; border-radius:6px; font-weight:600; font-size:12px; text-transform:uppercase; letter-spacing:0.05em; }
        .status-due { display:inline-block; padding: 4px 12px; background:#fef3c7; color:#a16207; border-radius:6px; font-weight:600; font-size:12px; text-transform:uppercase; letter-spacing:0.05em; }
        .footer { margin-top:32px; padding-top: 14px; border-top:1px solid #eee; color:#888; font-size:11px; display:flex; justify-content:space-between; }
        @media print { .no-print { display:none; } body { padding: 20px; } }
      </style></head><body>
        <div class="brand-bar">
          <div>
            <div class="logo">MarkeeMix Studio</div>
            <small style="color:#666;">YouTube Operations CRM · GST included</small>
          </div>
          <div class="meta-r">
            <strong>Invoice ${inv.invoice_no}</strong>
            <div>Date: ${today}</div>
            <div>Period: ${inv.period_month}</div>
            <div style="margin-top:6px;" class="${inv.status === "paid" ? "status-paid" : "status-due"}">${inv.status}</div>
          </div>
        </div>
        <h2>Bill to</h2>
        <div class="bill-to">
          <strong>${inv.channel_title || inv.channel_id}</strong><br/>
          <small>Channel ID: ${inv.channel_id}</small>
        </div>
        <h2>Line items</h2>
        <table>
          <thead><tr><th>Description</th><th style="text-align:right;">Qty</th><th style="text-align:right;">Unit</th><th style="text-align:right;">Total</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="totals"><table>
          <tr><td>Subtotal</td><td style="text-align:right;">₹${inv.subtotal.toLocaleString("en-IN")}</td></tr>
          <tr><td>GST (18%)</td><td style="text-align:right;">₹${inv.tax.toLocaleString("en-IN")}</td></tr>
          <tr class="grand"><td>Total due</td><td style="text-align:right;">₹${inv.total.toLocaleString("en-IN")}</td></tr>
        </table></div>
        <div class="footer">
          <span>MarkeeMix Studio · markeemix.com</span>
          <span>Thanks for working with us 💜</span>
        </div>
        <div class="no-print" style="margin-top:24px;text-align:center;">
          <button onclick="window.print()" style="padding:10px 22px;font-size:14px;background:linear-gradient(135deg,#7c3aed,#a78bfa);color:#fff;border:0;border-radius:8px;cursor:pointer;font-weight:600;">
            Print / Save as PDF
          </button>
        </div>
      </body></html>
    `);
    w.document.close();
    setTimeout(() => { try { w.print(); } catch (e) { /* ignore */ } }, 400);
  };

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 16 }}>
          <span className="studio-eyebrow"><Receipt size={12}/> Billing &amp; invoices</span>
          <h1 className="studio-h1">
            Monthly invoices, <span className="studio-h1-accent">PDF-ready for your clients.</span>
          </h1>
          <p className="studio-sub">
            Generate this month's invoice per channel with one click. ₹999 base + ₹199 per extra team member + ₹499 advanced features (GST 18% included).
          </p>
          <div className="studio-hero-cta" style={{ flexWrap: "wrap", gap: 8 }}>
            <select
              className="studio-input"
              value={activeId}
              onChange={(e) => setActiveId(e.target.value)}
              data-testid="billing-channel-select"
              style={{ minWidth: 240 }}
            >
              <option value="">Pick a channel…</option>
              {channels.map((c) => <option key={c.channel_id} value={c.channel_id}>{c.title}</option>)}
            </select>
            <button
              className="studio-btn-primary"
              onClick={onGenerate}
              disabled={!activeId || genBusy}
              data-testid="billing-generate"
            >
              {genBusy ? <Loader2 size={16} className="ac-spin"/> : <Plus size={16}/>} Generate this month's invoice
            </button>
          </div>
        </div>

        {error && <div className="ac-error"><AlertTriangle size={16}/> {error}</div>}
        {loading && (
          <div className="studio-card studio-loading">
            <Loader2 size={22} className="ac-spin"/>
            <p>Loading invoices…</p>
          </div>
        )}

        {!loading && invoices.length === 0 && (
          <div className="studio-empty" data-testid="billing-empty">
            <div className="studio-empty-icon"><FileText size={36}/></div>
            <h2>No invoices yet</h2>
            <p>Click <strong>Generate this month's invoice</strong> for any owned channel above.</p>
          </div>
        )}

        {!loading && invoices.length > 0 && (
          <div className="studio-grid" data-testid="billing-list">
            {invoices.map((inv) => (
              <div key={inv._id} className="studio-channel-card" data-testid={`invoice-${inv._id}`}>
                <div className="studio-channel-head">
                  <div className="studio-channel-meta" style={{ flex: 1 }}>
                    <h3 style={{ fontSize: "1rem" }}>{inv.invoice_no}</h3>
                    <p>{inv.channel_title} · {inv.period_month}</p>
                  </div>
                  <span className={`studio-role studio-role--${inv.status === "paid" ? "designer" : "creator"}`}>
                    {inv.status}
                  </span>
                </div>
                <div style={{ padding: "12px 16px", fontSize: 22, fontWeight: 800, color: "var(--st-text)" }}>
                  ₹{(inv.total || 0).toLocaleString("en-IN")}
                  <small style={{ display: "block", fontSize: 12, color: "var(--st-text-muted)", fontWeight: 400, marginTop: 4 }}>
                    Subtotal ₹{(inv.subtotal || 0).toLocaleString("en-IN")} + GST ₹{(inv.tax || 0).toLocaleString("en-IN")}
                  </small>
                </div>
                <div className="studio-channel-foot" style={{ gap: 8 }}>
                  <button className="studio-btn-ghost" onClick={() => onDownload(inv)} data-testid={`invoice-download-${inv._id}`}>
                    <Download size={14}/> Download PDF
                  </button>
                  {inv.status !== "paid" && (
                    <button className="studio-btn-primary" onClick={() => onMarkPaid(inv._id)} data-testid={`invoice-mark-paid-${inv._id}`}>
                      <Check size={14}/> Mark paid
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
